'use client';

import { AuthApi, type FileDto, type User } from '@up2stay/up2promo-api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { UsernameInput } from '@/core/components/auth/username-input';
import { LogoUpload } from '@/core/components/settings/logo-upload';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsIdentityStrip } from '@/core/components/settings/settings-identity-strip';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useErrorMessage } from '@/core/hooks/use-error-message';

type ProfileDict = {
  title: string;
  description: string;
  usernameHelperText?: string;
  usernameChangeTitle?: string;
  usernameChangeDescription?: string;
  usernameChangeConfirm?: string;
  usernameChangeCancel?: string;
};

type AvatarDict = {
  label: string;
  description: string;
  upload: string;
  change: string;
  remove: string;
  uploaded: string;
  uploadFailed: string;
  acceptsImages: string;
  uploading: string;
  dragAndDrop: string;
};

type AuthLabels = {
  usernameLabel: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
};

type ProfileBasicsSectionProps = {
  sectionId: string;
  user: User;
  onUserUpdated: (user: User) => void;
  authDict: AuthLabels;
  profileDict: ProfileDict;
  avatarDict: AvatarDict;
  saveLabel: string;
  savingLabel: string;
  usernameTakenLabel: string;
};

function nameSnapshot(firstName: string, lastName: string): string {
  return JSON.stringify({
    firstName: firstName.trim(),
    lastName: lastName.trim(),
  });
}

function initialsFrom(user: User): string {
  const fromNames = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => part![0])
    .join('');
  if (fromNames) return fromNames.toUpperCase().slice(0, 2);
  return (user.username || user.email || '?').slice(0, 2).toUpperCase();
}

export function ProfileBasicsSection({
  sectionId,
  user,
  onUserUpdated,
  authDict,
  profileDict,
  avatarDict,
  saveLabel,
  savingLabel,
  usernameTakenLabel,
}: ProfileBasicsSectionProps) {
  const getErrorMessage = useErrorMessage();
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [username, setUsername] = useState(user.username || '');
  const [usernameError, setUsernameError] = useState<string | undefined>();
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [confirmUsernameOpen, setConfirmUsernameOpen] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(
    user.photo?.path,
  );

  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
    markError,
  } = useAutosaveCue();

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastSynced, setLastSynced] = useState(() =>
    nameSnapshot(user.firstName || '', user.lastName || ''),
  );

  const userSyncKey = [
    user.id,
    user.firstName,
    user.lastName,
    user.username,
    user.photo?.path,
  ].join('\0');
  const [prevUserSyncKey, setPrevUserSyncKey] = useState(userSyncKey);
  if (userSyncKey !== prevUserSyncKey) {
    setPrevUserSyncKey(userSyncKey);
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setUsername(user.username || '');
    setAvatarPreview(user.photo?.path);
    setLastSynced(nameSnapshot(user.firstName || '', user.lastName || ''));
  }

  const persistNames = useCallback(async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) return;

    const snapshot = nameSnapshot(firstName, lastName);
    if (snapshot === lastSynced) return;

    markSaving();
    try {
      const response = await AuthApi.update({
        body: {
          firstName: trimmedFirst,
          lastName: trimmedLast,
        },
      });
      if (response.data) {
        onUserUpdated(response.data);
        setLastSynced(
          nameSnapshot(
            response.data.firstName || '',
            response.data.lastName || '',
          ),
        );
      }
      markSaved();
    } catch (err) {
      markError();
      const error = err as AxiosError<{ errors?: Record<string, string> }>;
      const backendErrors = error.response?.data?.errors;
      if (backendErrors?.firstName || backendErrors?.lastName) {
        toast.error(getErrorMessage('invalidName'));
      } else {
        toast.error(getErrorMessage(undefined));
      }
    }
  }, [
    firstName,
    getErrorMessage,
    lastName,
    markError,
    markSaved,
    markSaving,
    lastSynced,
    onUserUpdated,
  ]);

  useEffect(() => {
    if (!firstName.trim() || !lastName.trim()) return;

    const snapshot = nameSnapshot(firstName, lastName);
    if (snapshot === lastSynced) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      void persistNames();
    }, 700);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [firstName, lastName, lastSynced, persistNames]);

  const persistPhoto = async (photo: FileDto | null) => {
    markSaving();
    try {
      const response = await AuthApi.update({
        body: {
          photo: photo as unknown as FileDto | undefined,
        },
      });
      if (response.data) {
        onUserUpdated(response.data);
        setAvatarPreview(response.data.photo?.path);
      }
      markSaved();
    } catch {
      markError();
      toast.error(avatarDict.uploadFailed);
    }
  };

  const handleAvatarUpload = (fileId: string, _preview: string) => {
    void persistPhoto({ id: fileId });
  };

  const handleAvatarRemove = () => {
    void persistPhoto(null);
  };

  const handleSaveUsername = async () => {
    const trimmed = username.trim();
    if (!trimmed) return;
    if (trimmed === (user.username || '')) return;

    setIsSavingUsername(true);
    setUsernameError(undefined);
    try {
      const response = await AuthApi.update({
        body: { username: trimmed },
      });
      if (response.data) {
        onUserUpdated(response.data);
        markIdle();
      }
    } catch (err) {
      const error = err as AxiosError<{ errors?: Record<string, string> }>;
      const code = error.response?.data?.errors?.username;
      if (code === 'usernameAlreadyTaken') {
        setUsernameError(usernameTakenLabel);
      } else {
        setUsernameError(getErrorMessage(code));
      }
    } finally {
      setIsSavingUsername(false);
    }
  };

  const displayName =
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    user.username ||
    user.email;

  const usernameDirty = username.trim() !== (user.username || '');

  return (
    <>
      <SettingsSection
        id={sectionId}
        title={profileDict.title}
        footer={profileDict.description}
        grouped
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <SettingsIdentityStrip
              surface="plain"
              photoPath={avatarPreview}
              displayName={displayName}
              username={user.username}
              email={user.email}
              initials={initialsFrom(user)}
              autosaveStatus={autosaveStatus}
              photoControls={
                <LogoUpload
                  label={avatarDict.label}
                  description={avatarDict.description}
                  currentPreview={avatarPreview}
                  onUpload={handleAvatarUpload}
                  onRemove={handleAvatarRemove}
                  compact
                  hideCaption
                />
              }
            />
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{authDict.firstNameLabel}</Label>
                <Input
                  id="firstName"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={authDict.firstNamePlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{authDict.lastNameLabel}</Label>
                <Input
                  id="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={authDict.lastNamePlaceholder}
                />
              </div>
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-2">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="username">{authDict.usernameLabel}</Label>
                {profileDict.usernameHelperText ? (
                  <SettingsInfoHint label={authDict.usernameLabel}>
                    {profileDict.usernameHelperText}
                  </SettingsInfoHint>
                ) : null}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <UsernameInput
                    id="username"
                    value={username}
                    onChange={setUsername}
                    firstName={firstName}
                    lastName={lastName}
                    error={usernameError}
                    excludeUserId={user.id}
                  />
                </div>
                {usernameDirty ? (
                  <Button
                    type="button"
                    onClick={() => setConfirmUsernameOpen(true)}
                    isLoading={isSavingUsername}
                    disabled={isSavingUsername}
                    className="sm:mt-0"
                  >
                    <Save className="size-4" />
                    {isSavingUsername ? savingLabel : saveLabel}
                  </Button>
                ) : null}
              </div>
              {usernameError ? (
                <p className="text-sm text-destructive">{usernameError}</p>
              ) : null}
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>
      <AlertDialog
        open={confirmUsernameOpen}
        onOpenChange={setConfirmUsernameOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {profileDict.usernameChangeTitle ?? 'Change your username?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {profileDict.usernameChangeDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {profileDict.usernameChangeCancel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmUsernameOpen(false);
                void handleSaveUsername();
              }}
            >
              {profileDict.usernameChangeConfirm ?? saveLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
